import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, Mail, Building, Clock, Phone, Cookie } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { openCookiesPanel } from './TarteauCitronLoader';
import PWAInstallButton from './PWAInstallButton';
import PWAQrCode from './PWAQrCode';

export default function Footer() {
  const { logo } = useSiteSettings();
  const { t } = useTranslation();
  return (
    <footer className="bg-[#1A3C34] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main footer */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 py-14">
          {/* Brand */}
          <div className="md:col-span-1">
            <img src={logo} alt="Flexi-location" className="h-8 w-auto rounded mb-4" />
            <p className="text-sm text-white/50 leading-relaxed">
              {t('footer.tagline')}
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-heading text-sm font-bold uppercase tracking-wider text-white/40 mb-4">{t('footer.navigation')}</h4>
            <ul className="space-y-2.5">
              {[
                { to: '/plan', label: t('nav.plan') },
                { to: '/calendrier', label: t('nav.calendar') },
                { to: '/abonnements', label: t('nav.subscriptions') },
                { to: '/evenements', label: t('nav.events') },
              ].map(link => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm text-white/60 hover:text-[#C77D63] transition-colors">{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-heading text-sm font-bold uppercase tracking-wider text-white/40 mb-4">{t('footer.legal')}</h4>
            <ul className="space-y-2.5">
              {[
                { to: '/mentions-legales', label: t('footer.mentions') },
                { to: '/cgv', label: t('footer.terms') },
                { to: '/confidentialite', label: t('footer.privacy') },
              ].map(link => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm text-white/60 hover:text-[#C77D63] transition-colors">{link.label}</Link>
                </li>
              ))}
              <li>
                <button
                  onClick={openCookiesPanel}
                  data-testid="manage-cookies-btn"
                  className="text-sm text-white/60 hover:text-[#C77D63] transition-colors inline-flex items-center gap-1.5"
                >
                  <Cookie className="w-3.5 h-3.5" />
                  {t('footer.manageCookies')}
                </button>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading text-sm font-bold uppercase tracking-wider text-white/40 mb-4">{t('footer.contact')}</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-2.5 text-sm text-white/60">
                <MapPin className="w-4 h-4 text-[#C77D63] mt-0.5 flex-shrink-0" />
                <span>304 Chaussee de Stockel<br />1200 Woluwe-Saint-Lambert</span>
              </div>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Flexi-location+304+Chauss%C3%A9e+de+Stockel+1200+Bruxelles"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-google-maps-link"
                className="inline-flex items-center gap-1.5 text-xs text-[#C77D63] hover:text-white transition-colors -mt-1 ml-6"
              >
                {t('footer.viewOnGoogleMaps', 'Voir sur Google Maps')} →
              </a>
              <div className="flex items-center gap-2.5 text-sm text-white/60">
                <Clock className="w-4 h-4 text-[#C77D63] flex-shrink-0" />
                <span>06:00 - 22:00</span>
              </div>
              <div className="flex items-center gap-2.5 text-sm text-white/60">
                <Mail className="w-4 h-4 text-[#C77D63] flex-shrink-0" />
                <a href="mailto:info@flexi-location.be" className="hover:text-[#C77D63] transition-colors">info@flexi-location.be</a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/30">
            &copy; {new Date().getFullYear()} Flexi-location. {t('footer.copyright')}.
          </p>
          <div className="flex items-center gap-4 text-xs text-white/30">
            <PWAQrCode />
            <PWAInstallButton />
            <span data-testid="footer-vat">BE 0477.795.373</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
